import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Container } from 'typescript-ioc'
import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'
import { AccountService, DomainService, JourneyService, NotificationService } from '../../../services'
import { PromptService } from '../../../services/prompt'
import { AppState } from '../../../stores/app'
import { getDomainCurrencySymbol, getEnabledDeliveryChannels } from '../../../_utils/domain'
import {
    SegmentValueInputContext,
    type JourneyOption,
    type PromptOption,
    type SegmentValueInputContextValue,
} from './segment-value-input-context'
import { createPageTagSource, type PageTagScope } from './page-tag-source'
import { createNotificationSource } from './notification-source'

export interface SegmentValueInputProviderProps {
    level: 'org' | 'domain'
    /** Present at domain level; the level owns which id it has. */
    domainId?: number
    orgId?: number
    /**
     * The fields the level offers. Read only to decide which vocabularies are worth
     * loading — a catalog with no keyword condition in it has no use for keywords.
     */
    fields: PQueryBuilderField[]
    /** The query as it currently stands. See the context field of the same name. */
    getDocument: () => Record<string, unknown>
    children: React.ReactNode
}

function readNames(response: { ok?: boolean; data?: Array<{ name?: string }> }): string[] {
    if (!response?.ok || !Array.isArray(response.data)) return []

    return response.data
        .map((entry) => entry?.name)
        .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

/**
 * Supplies the segment renderers with everything the query builder cannot.
 *
 * The keyword vocabulary is loaded once for the whole builder rather than once per
 * row: a segment can hold several keyword conditions, and a fetch inside the
 * renderer would repeat the same request for each of them and again every time a
 * row is reopened.
 */
export function SegmentValueInputProvider({
    level,
    domainId,
    orgId,
    fields,
    getDocument,
    children,
}: SegmentValueInputProviderProps) {
    const [keywords, setKeywords] = useState<string[]>([])
    const [keywordsLoading, setKeywordsLoading] = useState(false)
    const [prompts, setPrompts] = useState<PromptOption[]>([])
    const [promptsLoading, setPromptsLoading] = useState(false)
    const [journeys, setJourneys] = useState<JourneyOption[]>([])
    const [journeysLoading, setJourneysLoading] = useState(false)
    const [notificationTitles, setNotificationTitles] = useState<Record<string, string>>({})

    // A notification's title is resolved by the control that searched for it and is needed by
    // every row that names the same notification, collapsed rows included — so it is kept for the
    // whole builder rather than inside the control that happened to ask. Merged rather than
    // replaced: two rows resolve different ids, and neither knows about the other's.
    const recordNotificationTitles = useCallback((titles: Record<string, string>) => {
        setNotificationTitles((current) => {
            const missing = Object.entries(titles).filter(([id, title]) => current[id] !== title)
            if (missing.length === 0) return current

            return { ...current, ...Object.fromEntries(missing) }
        })
    }, [])

    // Nothing here can ask for a keyword unless the catalog offers a condition that
    // takes one, and the request costs the same whether or not anyone will read it.
    const needsKeywords = useMemo(() => fields.some((field) => field.inputKey === 'keyword-clicked'), [fields])

    // The prompt and notification catalogs are read the same way and for the same
    // reason as the keyword one: only where a condition that draws on them is on offer.
    const needsPrompts = useMemo(() => fields.some((field) => field.inputKey === 'prompt-picker'), [fields])
    const needsJourneys = useMemo(() => fields.some((field) => field.inputKey === 'journey-status'), [fields])
    const needsNotifications = useMemo(
        () => fields.some((field) => field.inputKey === 'notification-id-clicked'),
        [fields],
    )

    const services = useMemo(
        () => ({
            domain: Container.get(DomainService),
            account: Container.get(AccountService),
            appState: Container.get(AppState),
            prompt: Container.get(PromptService),
            journey: Container.get(JourneyService),
            notification: Container.get(NotificationService),
        }),
        [],
    )

    // A level with no id of its own cannot be asked for anything, so it is stated as
    // absent rather than as a scope pointing at nothing.
    const scope: PageTagScope | null = useMemo(() => {
        if (level === 'domain') return domainId === undefined ? null : { level, domainId }
        return orgId === undefined ? null : { level, orgId }
    }, [level, domainId, orgId])

    useEffect(() => {
        if (!scope || !needsKeywords) return

        let cancelled = false
        setKeywordsLoading(true)

        const load = async () => {
            try {
                const response =
                    scope.level === 'domain'
                        ? await services.domain.fetchKeywordsByDomainId(scope.domainId, { query: { pagination: 0 } })
                        : await services.account.fetchKeywords(scope.orgId, { query: { pagination: 0 } })

                if (!cancelled) setKeywords(readNames(response))
            } catch {
                // A vocabulary that could not be read settles as empty rather than
                // taking the builder down with it: every other condition in the
                // segment is still editable, and the service reports its own failures.
            } finally {
                if (!cancelled) setKeywordsLoading(false)
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [scope, needsKeywords, services])

    // The prompt list is loaded whole and up front. `include_deleted` is load-bearing:
    // a condition can target a prompt since deleted, and dropping deleted prompts from
    // the list would blank that row and drop what it targeted on the next save. Only a
    // domain owns prompts, so the read is domain-scoped and never runs above one.
    useEffect(() => {
        const domain = services.appState.currentDomain
        if (!needsPrompts || scope?.level !== 'domain' || !domain) return

        let cancelled = false
        setPromptsLoading(true)

        const load = async () => {
            try {
                const response = await services.prompt.fetchPromptGroupsByDomainId(domain.id, {
                    query: {
                        pagination: 0,
                        include_deleted: true,
                        // Every channel the domain has on, so a prompt is offered whatever
                        // it was created under rather than only the current channel's.
                        channels: getEnabledDeliveryChannels(domain).join(','),
                    },
                })

                if (cancelled) return

                const options: PromptOption[] = []
                for (const group of response.data ?? []) {
                    for (const prompt of group.prompts) {
                        options.push({
                            id: prompt.id,
                            name: prompt.name || prompt.computedName || group.name || 'Unknown Prompt',
                        })
                    }
                }

                setPrompts(options)
            } catch {
                // A list that could not be read settles empty rather than taking the
                // builder down: the service reports its own failures, and a stored id
                // still renders as itself where its name never arrived.
            } finally {
                if (!cancelled) setPromptsLoading(false)
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [scope, needsPrompts, services])

    // The journey list is loaded whole and up front, the same as the prompt list: a stored
    // journey-status condition holds only a journey id, and this is what resolves it to a name.
    // Only a domain owns journeys, so the read is domain-scoped and never runs above one.
    useEffect(() => {
        const domain = services.appState.currentDomain
        if (!needsJourneys || scope?.level !== 'domain' || !domain) return

        let cancelled = false
        setJourneysLoading(true)

        const load = async () => {
            try {
                const response = await services.journey.fetchAll({
                    query: { pagination: 0 },
                    showLoadingScreen: false,
                    cancellationKey: 'rb.ensureJourneys',
                })

                if (cancelled) return

                setJourneys(
                    (response.data ?? []).map((journey) => ({ id: journey.id, name: journey.name })),
                )
            } catch {
                // A list that could not be read settles empty rather than taking the builder
                // down: the service reports its own failures, and a stored id still renders as
                // itself where its name never arrived.
            } finally {
                if (!cancelled) setJourneysLoading(false)
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [scope, needsJourneys, services])

    // Built apart from the context it is handed out in. The tag select keys its paging
    // on this reader's identity, so a new one starts the list over and discards every
    // page the reader has scrolled through — and the context as a whole is rebuilt on
    // things that have no bearing on which tags a level has, the keyword vocabulary
    // arriving among them. Only the level it lists tags for may replace it.
    const pageTags = useMemo(() => (scope ? createPageTagSource(scope, services.domain) : undefined), [scope, services])

    // Only a domain owns notifications, and only a search reaches them, so the source
    // is built domain-scoped and absent above one. Built apart from the context so
    // rebuilding the context on an unrelated change does not restart a search in flight.
    const notifications = useMemo(
        () =>
            needsNotifications && scope?.level === 'domain'
                ? createNotificationSource(scope.domainId, services.notification)
                : undefined,
        [needsNotifications, scope, services],
    )

    const value: SegmentValueInputContextValue = useMemo(
        () => ({
            // Only a domain trades in a currency of its own. Above one there is no
            // single answer, and the field that would need one is not offered there.
            currencySymbol: scope?.level === 'domain' ? getDomainCurrencySymbol(services.appState.currentDomain) : '$',
            keywords,
            keywordsLoading,
            prompts,
            promptsLoading,
            journeys,
            journeysLoading,
            pageTags,
            notifications,
            notificationTitles,
            recordNotificationTitles,
            getDocument,
        }),
        [
            scope,
            keywords,
            keywordsLoading,
            prompts,
            promptsLoading,
            journeys,
            journeysLoading,
            getDocument,
            services,
            pageTags,
            notifications,
            notificationTitles,
            recordNotificationTitles,
        ],
    )

    return <SegmentValueInputContext.Provider value={value}>{children}</SegmentValueInputContext.Provider>
}
